import { useState, useMemo } from 'react'

import { useContentCategories } from '@/core/hooks/misc/useContentCategories'
import { useMusicGenres } from '@/core/hooks/misc/useMusicGenres'

export type FilterType = 'mood' | 'content_category' | 'music_genre'

export interface FilteredSection {
	id: string
	type: FilterType
	filterValue: string | number // mood string, content category id, or music genre id
	title: string
}

function sortByPosition<T extends { positionPlaylists: number | null }>(
	items: T[]
): T[] {
	return items
		.map((item, index) => ({ item, index }))
		.sort((a, b) => {
			const aPos = a.item.positionPlaylists
			const bPos = b.item.positionPlaylists
			if (aPos === null && bPos === null) return a.index - b.index
			if (aPos === null) return 1
			if (bPos === null) return -1
			return aPos - bPos
		})
		.map(({ item }) => item)
}

export function useFilteredPlaylistSections() {
	const { data: musicGenres = [] } = useMusicGenres()

	const { data: contentCategories = [] } = useContentCategories({
		config: {
			params: {
				per_page: 250,
			},
		},
	})

	const allSections = useMemo<FilteredSection[]>(() => {
		const genreSections: FilteredSection[] = musicGenres.map((genre) => ({
			id: `music-genre-${genre.id}`,
			type: 'music_genre' as FilterType,
			filterValue: genre.id,
			title: genre.name,
		}))

		const filtered = contentCategories.filter((c) => c.showInPlaylists)
		const sorted = sortByPosition(filtered)
		const categorySections: FilteredSection[] = sorted.map((category) => ({
			id: `content-category-${category.id}`,
			type: 'content_category' as FilterType,
			filterValue: category.id,
			title: category.getLocalizedName('en-US'),
		}))

		return [...genreSections, ...categorySections]
	}, [musicGenres, contentCategories])

	const [visibleCount, setVisibleCount] = useState(3)

	const visibleSections = useMemo(() => {
		return allSections.slice(0, visibleCount)
	}, [allSections, visibleCount])

	const hasMore = visibleCount < allSections.length

	const loadMore = () => {
		setVisibleCount((prev) => Math.min(prev + 3, allSections.length))
	}

	return {
		visibleSections,
		hasMore,
		loadMore,
		totalSections: allSections.length,
	}
}
